import React, { useState, useMemo, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { ImageOff } from 'lucide-react';
import Spinner from 'components/Spinner';
import SecurityScoreModal from 'components/Modal/SecurityScore';
import styles from './PortfolioHoldings.module.sass';

interface PortfolioHoldingsProps {
  loadingState?: boolean;
  holdings: HoldingData[];
  selectedWallet: string;
}

interface HoldingData {
  symbol: string;
  chain: string;
  usd_value: number;
  portfolio_percentage: number;
  usd_price: number;
  logo: string;
  security_score: number;
  wallet_address: string;
}

const theme = createTheme({
  palette: {
    mode: 'dark',
    common: {
      black: '#000',
      white: '#fff'
    }
  }
});

export const SecurityMetric: React.FC<{ score: number; label: string }> = ({ score, label }) => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  
  const [activeTab, setActiveTab] = useState('overview');
  
  const getStatusColor = (score: number) => {
    if (score <= 25) return '#F121C3';
    if (score <= 50) return '#C458E3';
    if (score <= 75) return '#98A4FF';
    return '#73F6FF';
  };
  
  const color = getStatusColor(score);
  const isCritical = score <= 50;
  const isNonOptimal = score < 80;
  
  return (
    <>
      <button 
        onClick={handleOpen}
        className={`
          ${styles.securityMetric} 
          ${isNonOptimal ? (isCritical ? styles.critical : styles.nonOptimal) : ''}
        `}
        style={{
          color: color,
          background: `rgba(${isCritical ? '241, 33, 195' : '115, 246, 255'}, 0.1)`,
          border: `1px solid ${color}`
        }}
      >
        <span>{score}/100</span>
        <span>
          {score >= 80 ? 'OPTIMAL' : 
           score >= 60 ? 'STABLE' : 
           score >= 40 ? 'COMPROMISED' : 
           'CRITICAL'}
        </span>
      </button>
      <SecurityScoreModal 
        open={open}
        onClose={handleClose}
        score={score}
        label={label}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
    </>
  );
};

const TokenImage: React.FC<{ url?: string; symbol: string }> = ({ url, symbol }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [url]);

  if (!url || hasError) {
    return (
      <div className={styles.tokenImageContainer}>
        <ImageOff size={14} color="#A0AEC0" />
      </div>
    );
  }

  return (
    <div className={styles.tokenImageContainer}>
      <img 
        src={url} 
        alt={`${symbol} token`}
        className={styles.tokenImage}
        onError={() => setHasError(true)}
      />
    </div>
  );
};

const PortfolioHoldings: React.FC<PortfolioHoldingsProps> = ({
  loadingState = false,
  holdings,
  selectedWallet
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const filteredHoldings = useMemo(() => {
    const holdingsArray = Array.isArray(holdings) ? holdings : [];
    
    return holdingsArray.filter(
      (obj: HoldingData) => obj?.wallet_address?.toLowerCase() === selectedWallet?.toLowerCase()
    );
  }, [holdings, selectedWallet]);

  const paginatedHoldings = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredHoldings.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredHoldings, page, rowsPerPage]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loadingState) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner />
      </div>
    );
  }

  if (!filteredHoldings.length) {
    return (
      <div className={styles.emptyState}>
        No holdings data available for this wallet
      </div>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <div style={{ height: '100%', maxWidth: '100%', flex: 1.2, minWidth: 0 }}>
        <div className={styles.tableContainer}>
          <div className={styles.gridOverlay} />
          <Table stickyHeader size="small" className={styles.tableFixedLayout}>
            <TableHead>
              <TableRow>
                <TableCell className={`${styles.tableHeaderCell} ${styles.columnIcon}`}></TableCell>
                <TableCell className={`${styles.tableHeaderCell} ${styles.columnAsset}`}>Asset</TableCell>
                <TableCell className={`${styles.tableHeaderCell} ${styles.columnValue}`}>Value</TableCell>
                <TableCell className={`${styles.tableHeaderCell} ${styles.columnWeight}`}>Weight</TableCell>
                <TableCell className={`${styles.tableHeaderCell} ${styles.columnPrice}`}>Price</TableCell>
                <TableCell className={`${styles.tableHeaderCell} ${styles.columnSecurity}`} id="security-score-header">Security Score</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedHoldings.map((holding: HoldingData) => (
                <TableRow className={styles.tableRow} key={`${holding.symbol}-${holding.chain}`}>
                  <TableCell className={styles.tableCell}>
                    <TokenImage url={holding.logo} symbol={holding.symbol} />
                  </TableCell>
                  <TableCell className={styles.tableCell}>{holding.symbol}</TableCell>
                  <TableCell className={styles.tableCell}>
                    <span className={styles.valueText}>
                      ${holding.usd_value.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </span>
                  </TableCell>
                  <TableCell className={styles.tableCell}>
                    <span className={styles.valueText}>
                      {holding.portfolio_percentage.toFixed(2)}%
                    </span>
                  </TableCell>
                  <TableCell className={styles.tableCell}>
                    <span className={styles.valueText}>
                      ${holding.usd_price.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </span>
                  </TableCell>
                  <TableCell className={styles.tableCell}>
                    <SecurityMetric 
                      score={holding.security_score}
                      label={holding.symbol}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow className={styles.tableFooterRow}>
                <TableCell colSpan={6}>
                  <TablePagination
                    className={styles.tablePagination}
                    component="div"
                    count={filteredHoldings.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                    labelRowsPerPage="Show"
                    labelDisplayedRows={({ from, to, count }) => 
                      `${from}-${to} of ${count !== -1 ? count : `${to}+`}`
                    }
                    classes={{ 
                      actions: styles.paginationActions 
                    }}
                  />
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </div>
      </div>
    </ThemeProvider>
  );
};

export default PortfolioHoldings;